import React, { useEffect, useState } from 'react';

import {
  ApiError,
  StepUpChallenge,
  apiPost,
  onStepUpRequired,
} from '../../../adapters/api-client/api';
import { Drawer, Field, Note } from '../../../design-system/ops';

/**
 * Re-challenge for C2/C3/C4 — DEC-A01-08, and new-country sign-in, DEC-A01-14.
 *
 * Opens when the API answers STEP_UP_REQUIRED. It proves the two factors and
 * records the time on the server, and that is all it does: the refused request
 * is not held here and is never sent again (DEC-A01-16). A mutation has to be
 * resubmitted by the operator against current state. A refused read can be
 * reloaded, which changes nothing.
 */
type Stage = 'form' | 'verified';

const StepUpPrompt: React.FC = () => {
  const [challenge, setChallenge] = useState<StepUpChallenge | null>(null);
  const [stage, setStage] = useState<Stage>('form');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () =>
      onStepUpRequired((next) => {
        // A page's several reads are refused together; one prompt answers them.
        setChallenge((current) => current ?? next);
        setStage('form');
      }),
    [],
  );

  const close = () => {
    setChallenge(null);
    setPassword('');
    setCode('');
    setError(null);
    setStage('form');
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiPost('/api/v1/auth/step-up', { password, code });
      setPassword('');
      setCode('');
      setStage('verified');
    } catch (e) {
      // A wrong code and a service that cannot answer are different problems:
      // only the first is fixed by typing again.
      const refused = e instanceof ApiError && e.status === 403;
      setError(
        refused
          ? 'La contraseña o el código no se verificaron. No se ha cambiado nada.'
          : 'No se pudo verificar ahora mismo. Inténtalo de nuevo en un momento.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (!challenge) return null;

  const isRead = challenge.method === 'GET';
  const canSubmit = !busy && password.length > 0 && /^\d{6}$/.test(code);

  return (
    <Drawer
      open
      onClose={close}
      title="Verificación reforzada"
      meta={
        challenge.reasonCode === 'NEW_COUNTRY' ? 'Inicio de sesión desde un país nuevo' : undefined
      }
      footer={
        stage === 'form' ? (
          <>
            <button className="btn btn-o" onClick={close} disabled={busy}>
              Cancelar
            </button>
            <button className="btn btn-p" onClick={submit} disabled={!canSubmit}>
              {busy ? 'Verificando…' : 'Verificar'}
            </button>
          </>
        ) : isRead ? (
          <button className="btn btn-p" onClick={() => window.location.reload()}>
            Recargar
          </button>
        ) : (
          <button className="btn btn-p" onClick={close}>
            Entendido
          </button>
        )
      }
    >
      {stage === 'verified' ? (
        <Note tone="warn">
          {isRead
            ? 'Verificación completa. Recarga para ver los datos.'
            : 'Verificación completa. La acción no se ejecutó: revísala y envíala de nuevo.'}
        </Note>
      ) : (
        <>
          <Note>
            {challenge.reasonCode === 'NEW_COUNTRY'
              ? 'Este acceso viene de un país nuevo para tu cuenta. Los datos protegidos siguen ocultos hasta que verifiques tu identidad.'
              : 'Esta acción necesita una verificación reciente. Lo que intentabas hacer no se ejecutó.'}
          </Note>
          {error ? <Note tone="bad">{error}</Note> : null}
          <Field label="Contraseña">
            <input
              id="step-up-password"
              className="inp"
              type="password"
              autoComplete="current-password"
              aria-label="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Field label="Código del autenticador">
            <input
              id="step-up-code"
              className="inp"
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="Código del autenticador"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.trim())}
            />
          </Field>
        </>
      )}
    </Drawer>
  );
};

export default StepUpPrompt;
